import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  FormGroup,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import UserInfoModal from "../../components/Common/UserInfoModal";
import UserBroadcastsModal from "./helper/userBroadcastsModal";
import { getUserModerationList } from "./helper/userModerationApi";

const UserModerationPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [broadcastsModal, setBroadcastsModal] = useState(false);
  const [selectedUserForBroadcasts, setSelectedUserForBroadcasts] = useState(null);

  // Filter states
  const [username, setUsername] = useState("");
  const [userId, setUserId] = useState("");
  const [isSuspended, setIsSuspended] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const usernameTimeoutRef = useRef(null);
  const userIdTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  // Debounced values
  const [debouncedUsername, setDebouncedUsername] = useState(null);
  const [debouncedUserId, setDebouncedUserId] = useState(null);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.userId,
        sortable: true,
        cell: (row) => (
          <div>
            <div
              className="text-primary cursor-pointer"
              onClick={() => handleUserIdClick(row.userId)}
              style={{ cursor: "pointer" }}
            >
              {row.userId}
            </div>
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Username</span>,
        selector: (row) => row.username,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Age</span>,
        selector: (row) => row.age || "-",
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Phone Verified</span>,
        selector: (row) => row.phoneVerified,
        sortable: true,
        cell: (row) => (
          <Badge color={row.phoneVerified ? "success" : "danger"}>
            {row.phoneVerified ? "Yes" : "No"}
          </Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Gender</span>,
        selector: (row) => row.basicInfo?.gender || "-",
        sortable: true,
        cell: (row) => {
          const gender = row.basicInfo?.gender;
          if (gender === "1") return "Male";
          if (gender === "2") return "Female";
          return gender || "-";
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Created At</span>,
        selector: (row) => row.created_at,
        sortable: true,
        cell: (row) => {
          if (!row.created_at) return "-";
          const date = new Date(row.created_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Actions</span>,
        cell: (row) => (
          <Button
            color="info"
            size="sm"
            onClick={() => handleViewBroadcasts(row)}
            className="px-3"
          >
            <i className="mdi mdi-broadcast me-1"></i>View Broadcasts
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    []
  );

  const handleUserIdClick = useCallback((userId) => {
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleViewBroadcasts = useCallback((user) => {
    setSelectedUserForBroadcasts({
      userId: user.userId,
      username: user.username,
    });
    setBroadcastsModal(true);
  }, []);

  const fetchList = useCallback(async (debouncedUsername = null, debouncedUserId = null) => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        keyword: debouncedUsername !== null ? debouncedUsername : (username || undefined),
        userId: debouncedUserId !== null ? debouncedUserId : (userId || undefined),
        dateRange: dateRange || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        isSuspended: isSuspended !== "" ? isSuspended : undefined,
      };

      const result = await getUserModerationList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching user moderation list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [username, userId, dateRange, startDate, endDate, isSuspended, currentPage, limit]);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
      if (userIdTimeoutRef.current) {
        clearTimeout(userIdTimeoutRef.current);
      }
    };
  }, []);

  // Debounce username search
  useEffect(() => {
    if (usernameTimeoutRef.current) {
      clearTimeout(usernameTimeoutRef.current);
    }

    usernameTimeoutRef.current = setTimeout(() => {
      setDebouncedUsername(username);
    }, 1000);

    return () => {
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
    };
  }, [username]);

  // Debounce userId search
  useEffect(() => {
    if (userIdTimeoutRef.current) {
      clearTimeout(userIdTimeoutRef.current);
    }

    userIdTimeoutRef.current = setTimeout(() => {
      setDebouncedUserId(userId);
    }, 1000);

    return () => {
      if (userIdTimeoutRef.current) {
        clearTimeout(userIdTimeoutRef.current);
      }
    };
  }, [userId]);

  // Fetch when debounced username changes
  useEffect(() => {
    if (hasInitialFetchedRef.current && debouncedUsername !== null) {
      setCurrentPage(1);
      fetchList(debouncedUsername, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUsername]);

  // Fetch when debounced userId changes
  useEffect(() => {
    if (hasInitialFetchedRef.current && debouncedUserId !== null) {
      setCurrentPage(1);
      fetchList(null, debouncedUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUserId]);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch when other filters change
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, startDate, endDate, isSuspended]);

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case "username":
        setUsername(value);
        break;
      case "userId":
        setUserId(value);
        break;
      case "isSuspended":
        setIsSuspended(value);
        break;
      case "dateRange":
        setDateRange(value);
        if (value !== "custom") {
          setStartDate("");
          setEndDate("");
        }
        break;
      case "startDate":
        setStartDate(value);
        if (value) setDateRange("custom");
        break;
      case "endDate":
        setEndDate(value);
        if (value) setDateRange("custom");
        break;
      default:
        break;
    }
  };

  const clearFilters = useCallback(() => {
    setUsername("");
    setUserId("");
    setIsSuspended("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="User Moderation" breadcrumbItem="User Moderation" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-account-multiple me-2"></i>User Moderation
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label>Username</Label>
                        <Input
                          type="text"
                          placeholder="Search by username..."
                          value={username}
                          onChange={(e) => handleFilterChange("username", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>User ID</Label>
                        <Input
                          type="text"
                          placeholder="Search by user ID..."
                          value={userId}
                          onChange={(e) => handleFilterChange("userId", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Is Suspended</Label>
                        <Input
                          type="select"
                          value={isSuspended}
                          onChange={(e) => handleFilterChange("isSuspended", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="true">Suspended</option>
                          <option value="false">Not Suspended</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date Range</Label>
                        <Input
                          type="select"
                          value={dateRange}
                          onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                        >
                          <option value="">None</option>
                          <option value="last_24_hours">Last 24 Hours</option>
                          <option value="last_7_days">Last 7 Days</option>
                          <option value="last_30_days">Last 30 Days</option>
                          <option value="custom">Custom Range</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    {(dateRange === "custom" || (startDate || endDate)) && (
                      <>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={startDate}
                              onChange={(e) => handleFilterChange("startDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={endDate}
                              onChange={(e) => handleFilterChange("endDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </>
                    )}
                    <Col md={12}>
                      <Button color="secondary" onClick={clearFilters} className="me-2">
                        <i className="mdi mdi-filter-remove me-1"></i>Clear Filters
                      </Button>
                    </Col>
                  </Row>

                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-account-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No users found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Refresh the list after user action
          fetchList();
        }}
      />

      <UserBroadcastsModal
        isOpen={broadcastsModal}
        toggle={() => {
          setBroadcastsModal(false);
          setSelectedUserForBroadcasts(null);
        }}
        userId={selectedUserForBroadcasts?.userId}
        username={selectedUserForBroadcasts?.username}
      />
    </React.Fragment>
  );
};

export default UserModerationPage;

